import { AdTypeOption, AdTypeOptions } from '@/_shared/types'
import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { useSidebar } from '@/components/shadcn/sidebar'
import { AdCard } from '@/components/templates/AdCard'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions.tsx'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer' // <-- import useInView

export const Route = createFileRoute('/feeds/saved/ad-templates')({
  component: All,
})

function All() {
  const navigate = useNavigate()
  const { open: sidebarOpen } = useSidebar()

  const {
    data: savedAdTemplatesPages,
    isLoading: templatesLoading,
    isError: templatesError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = trpc.getAllSavedAdTemplatesPaginated.useInfiniteQuery(
    { limit: 20 },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { squareRef, desiredCardWidth, columns, gutterWidth } = useCardLayoutWithImpressions()

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      if (hasNextPage && !isFetchingNextPage && (savedAdTemplatesPages?.pages?.at(-1)?.results?.length ?? 0) >= 20) {
        fetchNextPage()
      }
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])
  if (templatesError) {
    return (
      <div className={'px-0'}>
        <ErrorDisplay />
      </div>
    )
  }

  if (templatesLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  const savedAdTemplates = savedAdTemplatesPages?.pages.flatMap((page) => page.results) || []

  const noTemplatesAvailable = savedAdTemplates.length === 0

  return (
    <Stack className="gap-3 lg:gap-8">
      <Stack className="gap-3 lg:gap-8">
        <div className="flex justify-between">
          <div>
            <Text weight="semibold" size="xxl">
              {sidebarOpen ? 'Kept Safe. Ready Anytime.' : 'All Saved: Kept Safe. Ready Anytime.'}
            </Text>
            <p className="text-sm lg:text-base text-thememutedforeground mt-2">
              Store and revisit your favorites whenever inspiration strikes.
            </p>
          </div>

          <div className="flex items-center gap-2 justify-end flex-wrap">
            <FeatureTabs
              tabItems={[
                { name: 'Ads', link: '/feeds/saved/ad-templates' },
                { name: 'Landers', link: '/feeds/saved/ad-landers' },
                { name: 'Emails', link: '/feeds/saved/emails' },
              ]}
            />
            <Select
              value={AdTypeOptions.ONLY_TEMPLATES}
              onValueChange={(value: AdTypeOption) => {
                if (value === AdTypeOptions.ALL_SAVED) {
                  navigate({
                    to: `/feeds/saved/ads`,
                  })
                }
              }}
            >
              <SelectTrigger className="w-33 h-8 max-h-8">
                <SelectValue placeholder="Ad Type" className="rounded-md" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(AdTypeOptions).map((item) => (
                    <SelectItem key={item} value={item} className="py-1.5">
                      {item}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Stack>

      <div className="">
        {noTemplatesAvailable ? (
          <div className="lg:h-96 flex justify-center items-center">
            <p>No ad added to this collection</p>
          </div>
        ) : (
          <div ref={squareRef} className="relative w-full pb-10">
            <TypedResponsiveMasonry columnsCountBreakPoints={columns ? { 0: columns } : {}}>
              <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                {/* Render saved templates */}
                {savedAdTemplates.map((ad) => (
                  <AdCard
                    key={ad?.atID}
                    showTemplateButton
                    typeof=""
                    adData={{
                      Ad: { ...ad, Liked: true },
                      IsLocked: false,
                      IsPublic: false,
                      brandName: undefined,
                      shouldInvalidateCache: false,
                      DesiredWidth: desiredCardWidth,
                    }}
                  />
                ))}
              </MasonryComponent>
            </TypedResponsiveMasonry>

            {/* Bottom scroll loader */}
            {hasNextPage && (
              <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
                {isFetchingNextPage && <Loader />}
              </div>
            )}
          </div>
        )}
      </div>
    </Stack>
  )
}
